import { cn } from "~/lib/utils";
import * as SwitchPrimitives from "@radix-ui/react-switch";

// // // //

interface Props {
    checked: boolean;
    label?: string;
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    color?: "gray" | "blue" | "green" | "orange" | "red";
    onChange: (updatedValue: boolean) => void;
}

/**
 * Renders a Toggle switch component
 *
 * ENHANCEMENT - update props.size to match sizes of other form elements
 */
export function Toggle(props: Props) {
    const {
        checked,
        label = "",
        size = "md",
        onChange,
        color = "gray",
    } = props;

    // Map size to switch dimensions
    const switchConfig = {
        xs: {
            rootClass: "h-3 w-8",
            thumbClass: "h-2.5 w-2.5",
            translate: "translate-x-4.5",
        },
        sm: {
            rootClass: "h-4 w-9",
            thumbClass: "h-3.5 w-3.5",
            translate: "translate-x-4.5",
        },
        md: {
            rootClass: "h-5 w-10",
            thumbClass: "h-4 w-4",
            translate: "translate-x-5",
        },
        lg: {
            rootClass: "h-6 w-11",
            thumbClass: "h-5 w-5",
            translate: "translate-x-5",
        },
        xl: {
            rootClass: "h-7 w-12",
            thumbClass: "h-6 w-6",
            translate: "translate-x-5",
        },
    }[size];

    // Map color to background classes
    const colorClasses = {
        ["gray"]: "data-[state=checked]:bg-slate-600",
        ["blue"]: "data-[state=checked]:bg-blue-500",
        ["green"]: "data-[state=checked]:bg-green-600",
        ["orange"]: "data-[state=checked]:bg-amber-600",
        ["red"]: "data-[state=checked]:bg-red-600",
    }[color];

    return (
        <div className="flex items-center">
            {label !== "" && (
                <label
                    className={cn(
                        "mr-2 cursor-pointer select-none text-foreground font-sans font-normal",
                        {
                            "text-[0.718rem]!": size === "xs",
                            "label-[0.812rem]!": size === "sm",
                            "label-03": size === "md",
                            "text-base": size === "lg",
                            "text-lg": size === "xl",
                        },
                    )}
                >
                    {label}
                </label>
            )}
            <SwitchPrimitives.Root
                checked={checked}
                onCheckedChange={onChange}
                className={cn(
                    "peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50",
                    switchConfig.rootClass,
                    colorClasses,
                    "data-[state=unchecked]:bg-muted",
                )}
            >
                <SwitchPrimitives.Thumb
                    className={cn(
                        "pointer-events-none block rounded-full bg-card border border-slate-400 shadow-sm ring-0 transition-transform data-[state=unchecked]:translate-x-0",
                        switchConfig.thumbClass,
                        `data-[state=checked]:${switchConfig.translate}`,
                    )}
                />
            </SwitchPrimitives.Root>
        </div>
    );
}
